/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { AttachmentPreviewModal } from '../../shared/attachments/components/AttachmentPreviewModal';
import { pickAttachmentInteractive } from '../../shared/attachments/infra/pickAttachmentFile';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import {
  getCustomerMovementDisplayLabel,
  getCustomerPaymentMethodLabel,
} from '../domain/customerMovementLabels';
import type { CustomerMovementRow } from '../domain/customerTypes';
import {
  createCustomerMovementAttachmentUrl,
  deleteCustomerMovementAttachment,
  listCustomerMovementAttachments,
  saveCustomerMovementAttachment,
  type CustomerMovementAttachmentRow,
} from '../infra/customerMovementAttachmentsRepo';
import { formatMoney } from '../../shared/format/public-api';

type Props = {
  visible: boolean;
  movement: CustomerMovementRow | null;
  /** `canCollect` — habilita adjuntar/borrar comprobantes. */
  canEdit: boolean;
  onClose: () => void;
};

/**
 * Detalle de un movimiento de cuenta (cobro o consumo manual).
 * Para cobros permite ver, adjuntar y borrar comprobantes de evidencia
 * (foto del cheque, comprobante de transferencia). Los adjuntos son
 * best-effort: el movimiento financiero ya quedó registrado.
 */
export function CustomerMovementDetailModal({ visible, movement, canEdit, onClose }: Props) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const user = useAuthStore((s) => s.user);

  const [attachments, setAttachments] = useState<CustomerMovementAttachmentRow[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewMime, setPreviewMime] = useState('application/octet-stream');

  const isPayment = movement?.type === 'payment';
  const canManageAttachments = canEdit && isPayment;

  const reloadAttachments = useCallback(async () => {
    if (!movement) {
      setAttachments([]);
      return;
    }
    setLoadingAttachments(true);
    try {
      const rows = await listCustomerMovementAttachments(movement.id);
      setAttachments(rows);
    } finally {
      setLoadingAttachments(false);
    }
  }, [movement?.id]);

  useEffect(() => {
    if (!visible || !movement) {
      setAttachments([]);
      setAttachmentError(null);
      setAttachmentBusy(false);
      setPendingDeleteId(null);
      setPreviewUri(null);
      setPreviewFileName('');
      setPreviewMime('application/octet-stream');
      return;
    }
    void reloadAttachments();
  }, [visible, movement?.id, reloadAttachments]);

  const handleAttach = useCallback(async () => {
    if (attachmentBusy || !movement) return;
    setAttachmentError(null);
    setAttachmentBusy(true);
    try {
      const picked = await pickAttachmentInteractive('customers');
      if (!picked) return;
      const orgId = await getSessionOrganizationId();
      if (!orgId) {
        setAttachmentError('No pude identificar la organización para adjuntar.');
        return;
      }
      await saveCustomerMovementAttachment({
        attachmentId: crypto.randomUUID(),
        movementId: movement.id,
        orgId,
        fileName: picked.fileName,
        mimeType: picked.mimeType,
        fileSize: picked.fileSize,
        fileBase64: picked.base64,
        createdBy: user?.id ?? null,
      });
      await reloadAttachments();
    } catch (e) {
      setAttachmentError(e instanceof Error ? e.message : String(e));
    } finally {
      setAttachmentBusy(false);
    }
  }, [attachmentBusy, movement?.id, reloadAttachments, user?.id]);

  const confirmDeleteAttachment = useCallback(async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!id) return;
    setAttachmentError(null);
    setAttachmentBusy(true);
    try {
      await deleteCustomerMovementAttachment(id);
      await reloadAttachments();
    } catch (e) {
      setAttachmentError(e instanceof Error ? e.message : String(e));
    } finally {
      setAttachmentBusy(false);
    }
  }, [pendingDeleteId, reloadAttachments]);

  const openAttachmentUrl = useCallback(async (attachment: CustomerMovementAttachmentRow) => {
    setAttachmentError(null);
    if (!attachment.fileUrl) {
      setAttachmentError('El comprobante todavía no está disponible.');
      return;
    }
    const url = await createCustomerMovementAttachmentUrl(attachment.fileUrl);
    if (!url) {
      setAttachmentError('No pude generar el enlace del comprobante.');
      return;
    }
    setPreviewFileName(attachment.fileName);
    setPreviewMime(attachment.mimeType);
    setPreviewUri(url);
  }, []);

  if (!movement) return null;

  const methodLabel = getCustomerPaymentMethodLabel(movement.paymentMethod);
  const check = movement.check;

  const body = (
    <>
      <View style={styles.sheetHeroBand}>
        <Text style={styles.sheetHeroKicker}>Movimiento</Text>
        <Text style={styles.sheetHeroTitle}>
          {getCustomerMovementDisplayLabel(movement)} · {formatMoney(movement.amount)}
        </Text>
        <Text style={styles.sheetHeroSubtitle}>
          {formatLocalDateTime(movement.createdAt)}
          {methodLabel ? ` · ${methodLabel}` : ''}
        </Text>
        {check && (check.number || check.drawer || check.bank || check.dueDate) ? (
          <Text style={styles.sheetHeroSubtitle}>
            Cheque
            {check.number ? ` N° ${check.number}` : ''}
            {check.dueDate ? ` · vence ${check.dueDate}` : ''}
            {check.drawer ? ` · ${check.drawer}` : ''}
            {check.bank ? ` · ${check.bank}` : ''}
          </Text>
        ) : null}
        {movement.note ? <Text style={styles.sheetHeroNote}>{movement.note}</Text> : null}
      </View>

      {isPayment ? (
        <>
          <Text style={modalExperience.formSectionTitle}>Comprobantes</Text>
          {loadingAttachments ? (
            <View style={modalExperience.stateBox}>
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={modalExperience.stateText}>Cargando…</Text>
            </View>
          ) : attachments.length > 0 ? (
            <View style={styles.attachmentList}>
              {attachments.map((a) => (
                <View key={a.id} style={styles.attachmentRow}>
                  <TouchableOpacity
                    onPress={() => void openAttachmentUrl(a)}
                    accessibilityRole="button"
                    accessibilityLabel={`Ver ${a.fileName}`}
                    style={styles.attachmentInfo}
                    testID={`customers.movement.attachment.${a.id}`}
                  >
                    <Ionicons
                      name={a.mimeType.startsWith('image/') ? 'image-outline' : 'document-text-outline'}
                      size={20}
                      color={ticketColors.ink2}
                    />
                    <View style={styles.attachmentTextCol}>
                      <Text style={styles.attachmentFileName} numberOfLines={1}>
                        {a.fileName}
                      </Text>
                      <Text style={styles.attachmentMeta}>
                        {a.label ? `${a.label} · ` : ''}Ver adjunto
                      </Text>
                    </View>
                  </TouchableOpacity>
                  {canManageAttachments ? (
                    <TouchableOpacity
                      onPress={() => setPendingDeleteId(a.id)}
                      disabled={attachmentBusy}
                      accessibilityRole="button"
                      accessibilityLabel={`Borrar ${a.fileName}`}
                      style={styles.deleteBtn}
                      testID={`customers.movement.attachment.delete.${a.id}`}
                    >
                      <Ionicons name="trash-outline" size={16} color={ticketColors.bad} />
                    </TouchableOpacity>
                  ) : null}
                </View>
              ))}
            </View>
          ) : (
            <ModalStatePanel
              empty
              emptyTitle="Sin comprobantes"
              emptyMessage="Este cobro no tiene archivos adjuntos."
            />
          )}
          {attachmentError ? <Text style={styles.attachError}>{attachmentError}</Text> : null}
          {canManageAttachments ? (
            <TouchableOpacity
              onPress={() => void handleAttach()}
              disabled={attachmentBusy}
              accessibilityRole="button"
              accessibilityLabel="Adjuntar comprobante"
              style={styles.attachCta}
              testID="customers.movement.attach-cta"
            >
              <Ionicons name="attach-outline" size={18} color={ticketColors.gold} />
              <Text style={styles.attachCtaText}>
                {attachmentBusy ? 'Subiendo…' : attachments.length > 0 ? 'Adjuntar otro comprobante' : 'Adjuntar comprobante'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}
    </>
  );

  const previewModal = (
    <AttachmentPreviewModal
      visible={previewUri != null}
      uri={previewUri}
      fileName={previewFileName}
      mimeType={previewMime}
      onClose={() => setPreviewUri(null)}
    />
  );

  const deleteConfirm = (
    <OperationalConfirmModal
      visible={pendingDeleteId != null}
      presentation="overlay"
      title="Borrar comprobante"
      message="Se borra el archivo adjunto. El cobro no cambia."
      confirmLabel="Borrar"
      destructive
      loading={attachmentBusy}
      onClose={() => setPendingDeleteId(null)}
      onConfirm={() => void confirmDeleteAttachment()}
    />
  );

  const cardContent = (
    <>
      {!isDesktop ? <View style={styles.grabber} /> : null}
      <TicketModalHeader
        kicker="Clientes"
        title="Detalle del movimiento"
        onClose={onClose}
        compact={!isDesktop}
      />
      <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
        {body}
      </ScrollView>
      {deleteConfirm}
    </>
  );

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[styles.sheetBackdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Cerrar" />
          <View
            style={[
              styles.sheetCard,
              styles.sheetCardDesktop,
              { paddingBottom: Math.max(insets.bottom, spacing.sm) },
            ]}
          >
            {cardContent}
          </View>
          {previewModal}
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.sheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Cerrar" />
        <View style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
          {cardContent}
        </View>
        {previewModal}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    maxHeight: '80%',
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetCardDesktop: {
    maxWidth: 520,
    width: '92%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    maxHeight: '85%',
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.sm,
  },
  sheetHeroBand: {
    ...ticketSurfaces.paperSheet,
    backgroundColor: ticketColors.tile,
    padding: spacing.md,
    gap: spacing.xs,
  },
  sheetHeroKicker: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sheetHeroTitle: {
    ...typography.body,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  sheetHeroSubtitle: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  sheetHeroNote: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontStyle: 'italic',
  },
  attachmentList: {
    gap: spacing.xs,
  },
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  attachmentInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
    minWidth: 0,
  },
  attachmentTextCol: {
    flex: 1,
    minWidth: 0,
  },
  attachmentFileName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  attachmentMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  deleteBtn: {
    padding: spacing.xs,
  },
  attachError: {
    ...typography.caption,
    color: ticketColors.bad,
  },
  attachCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderStyle: 'dashed',
    backgroundColor: ticketColors.paper,
  },
  attachCtaText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '600',
  },
});
